package dev.youndie.site.pages.blog

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initThreeKtorEnginesUnderOneProtocolPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("blog/ThreeKtorEnginesUnderOneProtocol.md", FrontMatterElement.Builder { addScalar("title", "Handing a request to a handler costs 39 % of a Ktor CIO service's CPU — and 1 % on Netty"); addScalar("searchTitle", "Ktor server engines compared: CIO vs Netty vs Jetty, CPU per request, Dispatchers.IO parallelism"); addScalar("date", "2026-09-11"); addScalar("library", "zavarnik"); addScalar("description", "The same service on CIO, Netty and Jetty, one -D apart: 166, 82 and 146 microseconds of CPU per request. A third of CIO's CPU is the queue inside Dispatchers.IO; one system property removes most of it and costs 7.7x on a handler that blocks. The stand, the numbers and the limits."); }))
}

@Page("/blog/three-ktor-engines-under-one-protocol")
@Layout(".components.layouts.MarkdownLayout")
@Composable
fun ThreeKtorEnginesUnderOneProtocolPage() {
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A ")
        com.varabyte.kobweb.silk.components.navigation.Link("/blog/user-code-share-of-a-ktor-service") {
            org.jetbrains.compose.web.dom.Text("previous post")
        }
        org.jetbrains.compose.web.dom.Text(" measured what share of a Ktor service's profile user code owns and found 1–4 %. The biggest number in that profile belonged to nobody in the question: between a fifth and a third of ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("/business")
        }
        org.jetbrains.compose.web.dom.Text(" CPU sat in the coroutine dispatcher's queue, and the verdict — \"a property of CIO under this load\" — was drawn from the only engine the stand had. This post is that comparison: the same service, the same jars, the same process on CIO, Netty and Jetty, one ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-Dbench.engine")
        }
        org.jetbrains.compose.web.dom.Text(" apart. The stand, the harness and every run summary are in ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/zavarnik") {
            org.jetbrains.compose.web.dom.Text("zavarnik")
        }
        org.jetbrains.compose.web.dom.Text(" under ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("bench/")
        }
        org.jetbrains.compose.web.dom.Text(", the full write-up in ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("docs/research/research-engines.md")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-stand") }) {
        org.jetbrains.compose.web.dom.Text("The stand")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Service: Ktor 3.5.2, kotlinx.serialization 1.11.0, kotlinx.coroutines 1.11.0, slf4j-simple. Three endpoints: ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("/echo")
            }
            org.jetbrains.compose.web.dom.Text(", ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("/items")
            }
            org.jetbrains.compose.web.dom.Text(" (JSON CRUD over an in-memory store), ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("/business")
            }
            org.jetbrains.compose.web.dom.Text(" (collection chains, a regex, three disabled ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("logger.debug")
            }
            org.jetbrains.compose.web.dom.Text(" templates per request).")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Engines as they ship: CIO, Netty 4.2.16, Jetty 12.0.35 through ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("ktor-server-jetty-jakarta")
            }
            org.jetbrains.compose.web.dom.Text(". No engine configuration is touched, so ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("connectionGroupSize")
            }
            org.jetbrains.compose.web.dom.Text(" = ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("workerGroupSize")
            }
            org.jetbrains.compose.web.dom.Text(" = ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("parallelism / 2 + 1")
            }
            org.jetbrains.compose.web.dom.Text(" and ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("callGroupSize")
            }
            org.jetbrains.compose.web.dom.Text(" = ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("parallelism")
            }
            org.jetbrains.compose.web.dom.Text(", the Ktor defaults.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Load: ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("oha")
            }
            org.jetbrains.compose.web.dom.Text(", 64 keep-alive connections; JVM pinned to cores 0–7, generator to 8–15; ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("-Xms1g -Xmx1g -XX:+UseG1GC")
            }
            org.jetbrains.compose.web.dom.Text(".")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Profiler: async-profiler 4.5, 120 s of ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("cpu")
            }
            org.jetbrains.compose.web.dom.Text(" then 120 s of ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("alloc")
            }
            org.jetbrains.compose.web.dom.Text(", after 60 s of warm-up and a 120 s profiler-free window that the throughput and cost numbers come from.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Machine: Ubuntu 24.04 in WSL2, Core Ultra 7 255HX, OpenJDK 25.0.4.")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("One thing in that protocol used to be a lie. ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("taskset -pc <pid>")
        }
        org.jetbrains.compose.web.dom.Text(" sets the affinity of a single ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("thread")
        }
        org.jetbrains.compose.web.dom.Text(": after it, 49 of the JVM's 50 threads still read ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Cpus_allowed_list: 0-19")
        }
        org.jetbrains.compose.web.dom.Text(", and the process burned 12.6 cores while nominally capped at 8. Every run of the earlier post — including the configuration its text calls pinned — had the service and the load generator fighting over the same 20 cores. Pinning on the ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("exec")
        }
        org.jetbrains.compose.web.dom.Text(" is inherited by every thread, and the JVM then sizes its pools to the cores it can see. The old protocol is kept as a control below, and it reproduces the published numbers.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-a-request-costs") }) {
        org.jetbrains.compose.web.dom.Text("What a request costs")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("/business")
        }
        org.jetbrains.compose.web.dom.Text(", 64 connections, from the clean window without profilers. The absolute number is ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("utime+stime")
        }
        org.jetbrains.compose.web.dom.Text(" of the process over that window divided by the requests served in it.")
    }
    org.jetbrains.compose.web.dom.Table {
        org.jetbrains.compose.web.dom.Thead {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("Engine")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("rps")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("p50")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("p99")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("CPU µs/request")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("ctx switches/request")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("threads")
                }
            }
        }
        org.jetbrains.compose.web.dom.Tbody {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("CIO")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("43 472")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("0.83 ms")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("13.50 ms")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("166")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("2.53")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("103")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("Netty")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("79 337")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("0.73 ms")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("2.05 ms")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("82")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("0.63")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("43")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("Jetty")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("52 383")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("1.13 ms")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("2.94 ms")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("146")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("9.60")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("206")
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("On ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("/echo")
        }
        org.jetbrains.compose.web.dom.Text(" the same order holds at 125 / 56 / 88 µs, on ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("/items")
        }
        org.jetbrains.compose.web.dom.Text(" at 142 / 88 / 120. All three respond with identical bytes — same status, same ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Content-Length")
        }
        org.jetbrains.compose.web.dom.Text(", no chunked framing — so the difference is machinery, not payload. Netty's rps understates the gap: at 100 023 rps it used 5.6 of its 8 cores while the generator used 3.0 of its own, so its throughput there is bounded by the offered concurrency (64 connections at 0.58 ms is ~110 k rps by Little's law), not by CPU. CIO, at 7.2 cores of 8, is bounded by CPU.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("where-the-cpu-goes") }) {
        org.jetbrains.compose.web.dom.Text("Where the CPU goes")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("By owner — the first frame from the leaf that is neither JDK nor JVM — on ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("/business")
        }
        org.jetbrains.compose.web.dom.Text(":")
    }
    org.jetbrains.compose.web.dom.Table {
        org.jetbrains.compose.web.dom.Thead {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("Owner")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("CIO")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("Netty")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("Jetty")
                }
            }
        }
        org.jetbrains.compose.web.dom.Tbody {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("kotlinx.coroutines")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("56.9 %")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("8.9 %")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("29.6 %")
                    }
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("the engine itself")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("—")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("43.1 %")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("15.5 %")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("Ktor")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("21.0 %")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("16.0 %")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("7.1 %")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("user code")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("3.1 %")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("6.1 %")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("3.3 %")
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The leaf frames say it more plainly. CIO: ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("LockFreeTaskQueueCore.removeFirstOrNull")
        }
        org.jetbrains.compose.web.dom.Text(" 17.3 %, ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("LimitedDispatcher.obtainTaskOrDeallocateWorker")
        }
        org.jetbrains.compose.web.dom.Text(" 6.9 %, ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("LimitedDispatcher.dispatch")
        }
        org.jetbrains.compose.web.dom.Text(" 4.3 %. Netty: ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("writev")
        }
        org.jetbrains.compose.web.dom.Text(" 6.9 %, ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("__write")
        }
        org.jetbrains.compose.web.dom.Text(" 5.8 %, ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("epoll_wait")
        }
        org.jetbrains.compose.web.dom.Text(" 5.6 %, and the first application frame is ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Pricing.quote")
        }
        org.jetbrains.compose.web.dom.Text(" at 1.6 %. Jetty: ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("pthread_cond_signal")
        }
        org.jetbrains.compose.web.dom.Text(" 20.4 % plus libc 20.9 %, ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("LockSupport.park")
        }
        org.jetbrains.compose.web.dom.Text(" and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("unpark")
        }
        org.jetbrains.compose.web.dom.Text(" at 2 % each.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Summed into one bucket — everything that moves a request from an I/O thread to a handler and back — that is 33–48 % of self CPU on CIO across the three endpoints, 25–30 % on Jetty and 1–2.5 % on Netty.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The reason is in the engines' source, not in the profile. ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("CIOApplicationEngine")
        }
        org.jetbrains.compose.web.dom.Text(" runs both its engine and its user dispatcher on ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Dispatchers.IO")
        }
        org.jetbrains.compose.web.dom.Text(", and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Dispatchers.IO")
        }
        org.jetbrains.compose.web.dom.Text(" on the JVM is ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("UnlimitedIoScheduler.limitedParallelism(max(64, availableProcessors))")
        }
        org.jetbrains.compose.web.dom.Text(" — a ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("LimitedDispatcher")
        }
        org.jetbrains.compose.web.dom.Text(" holding one ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("LockFreeTaskQueue")
        }
        org.jetbrains.compose.web.dom.Text(" that up to 64 worker loops poll. Netty's ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("NettyDispatcher")
        }
        org.jetbrains.compose.web.dom.Text(" returns ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("false")
        }
        org.jetbrains.compose.web.dom.Text(" from ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("isDispatchNeeded")
        }
        org.jetbrains.compose.web.dom.Text(" when the code already runs on the channel's event loop, so most calls are never handed anywhere; what remains goes to that channel's own executor. Jetty's ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("JettyKtorHandler")
        }
        org.jetbrains.compose.web.dom.Text(" dispatches into its own ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("ThreadPoolExecutor")
        }
        org.jetbrains.compose.web.dom.Text(" over a ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("SynchronousQueue")
        }
        org.jetbrains.compose.web.dom.Text(" — a rendezvous, paid for in thread wake-ups rather than in polling.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("So the queue is not the price of coroutines. It is the price of one shared MPMC queue with 64 workers contending for its head.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-lever") }) {
        org.jetbrains.compose.web.dom.Text("The lever")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("If that is the mechanism, cutting the worker count to the core count must cut the cost — and must do nothing to Netty, which does not route calls through ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Dispatchers.IO")
        }
        org.jetbrains.compose.web.dom.Text(". Both halves hold. Throughput measured without profilers, three variants alternating, medians of three runs:")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-bash") }) { org.jetbrains.compose.web.dom.Text("""java -Dkotlinx.coroutines.io.parallelism=8 -jar service.jar
""") } }
    org.jetbrains.compose.web.dom.Table {
        org.jetbrains.compose.web.dom.Thead {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("Variant")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("rps median (runs)")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("p99")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("CPU µs/request")
                }
            }
        }
        org.jetbrains.compose.web.dom.Tbody {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("CIO, as it ships (")
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("io.parallelism")
                    }
                    org.jetbrains.compose.web.dom.Text(" = 64)")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("40 673 (40 536 · 40 673 · 42 909)")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("14.45 ms")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("176")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("CIO, ")
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("io.parallelism=16")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("68 382 (66 003 · 68 382 · 70 383)")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("5.90 ms")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("105")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("CIO, ")
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("io.parallelism=8")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("69 130")
                    }
                    org.jetbrains.compose.web.dom.Text(" (67 175 · 69 130 · 72 691)")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("2.45 ms")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("98")
                    }
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("That is +70 % throughput, −44 % CPU per request and a p99 six times lower, from one system property; the three runs of each variant do not overlap with the others on any of the three figures. The dispatch bucket falls with it: 38.9 % of self CPU at 64 workers, 23.2 % at 16, 15.4 % at 8. On Netty the same flag moves nothing — 82 889 rps against 79 337, 79 µs against 82.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-same-thing-without-an-engine") }) {
        org.jetbrains.compose.web.dom.Text("The same thing without an engine")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The explanation above — workers contending for the head of one queue — is read out of the sources and supported by everything the service does. It is also testable without Ktor: 64 coroutines that do nothing but resume on a dispatcher, four resumptions per operation, no sockets and no HTTP. On a two-core box, all variants saturating the same 1.77 cores:")
    }
    org.jetbrains.compose.web.dom.Table {
        org.jetbrains.compose.web.dom.Thead {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("Dispatcher")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("ops/s")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("CPU µs per op")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("CPU µs per dispatch")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("threads")
                }
            }
        }
        org.jetbrains.compose.web.dom.Tbody {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("Dispatchers.IO")
                    }
                    org.jetbrains.compose.web.dom.Text(", parallelism 64 (the default)")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("219 816")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("8.04")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("2.01")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("114")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("Dispatchers.IO")
                    }
                    org.jetbrains.compose.web.dom.Text(", ")
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("io.parallelism=8")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("1 802 299")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("1.00")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("0.25")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("25")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("Dispatchers.IO")
                    }
                    org.jetbrains.compose.web.dom.Text(", ")
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("io.parallelism=2")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("2 581 194")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("0.68")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("0.17")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("15")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("Dispatchers.IO.limitedParallelism(8)")
                    }
                    org.jetbrains.compose.web.dom.Text(", default left at 64")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("1 797 108")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("0.99")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("0.25")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("24")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("Dispatchers.IO.limitedParallelism(2)")
                    }
                    org.jetbrains.compose.web.dom.Text(", default left at 64")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("3 071 939")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("0.57")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("0.14")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("15")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("Dispatchers.Default")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("8 773 056")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("0.20")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("0.05")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("8")
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Same work, same cores; only the number of workers over the shared queue changes, and one dispatch costs eight times more at 64 workers than at eight. The fourth and fifth rows are the ones that matter for a library: a ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("limitedParallelism")
        }
        org.jetbrains.compose.web.dom.Text(" view sized at the call site buys exactly what the process-wide property buys — 0.99 µs against 1.00 — while leaving ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Dispatchers.IO")
        }
        org.jetbrains.compose.web.dom.Text(" alone for everything else in the process. Ktor cannot currently be asked for that: ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("CIOApplicationEngine")
        }
        org.jetbrains.compose.web.dom.Text(" holds its dispatcher in a private field, and a dispatcher supplied through the parent coroutine context is overridden, which is ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://youtrack.jetbrains.com/issue/KTOR-6797") {
            org.jetbrains.compose.web.dom.Text("KTOR-6797")
        }
        org.jetbrains.compose.web.dom.Text(", open since February 2024. ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://youtrack.jetbrains.com/issue/KTOR-6462") {
            org.jetbrains.compose.web.dom.Text("KTOR-6462")
        }
        org.jetbrains.compose.web.dom.Text(", \"clients and servers should use ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Dispatchers.IO.limitedParallelism(...)")
        }
        org.jetbrains.compose.web.dom.Text(" wherever possible\", is closed as fixed; in 3.5.2 and on ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("main")
        }
        org.jetbrains.compose.web.dom.Text(" the server CIO engine still dispatches into the shared ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Dispatchers.IO")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The last row is a separate observation: ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Dispatchers.Default")
        }
        org.jetbrains.compose.web.dom.Text(", which has no ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("LimitedDispatcher")
        }
        org.jetbrains.compose.web.dom.Text(" in the chain at all, is another five times cheaper per dispatch than the cheapest view of ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Dispatchers.IO")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-the-lever-costs") }) {
        org.jetbrains.compose.web.dom.Text("What the lever costs")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("io.parallelism")
        }
        org.jetbrains.compose.web.dom.Text(" is also how many threads CIO has to run handlers on. ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("/blocking")
        }
        org.jetbrains.compose.web.dom.Text(" is a handler that sleeps 5 ms — a JDBC call without the database — and it inverts the table:")
    }
    org.jetbrains.compose.web.dom.Table {
        org.jetbrains.compose.web.dom.Thead {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("Variant")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("rps median")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("p50")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("expected ceiling from thread count")
                }
            }
        }
        org.jetbrains.compose.web.dom.Tbody {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("CIO, as it ships")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("11 818")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("5.38 ms")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("64 / 5 ms = 12 800")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("CIO, ")
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("io.parallelism=8")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("1 538")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("37.89 ms")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("8 / 5 ms = 1 600")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("Netty, as it ships")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("1 552")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("41.24 ms")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("callGroupSize")
                    }
                    org.jetbrains.compose.web.dom.Text(" = 8 → 1 600")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("Jetty, as it ships")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("11 945")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("5.32 ms")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("pool grows to ")
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("callGroupSize × 8")
                    }
                    org.jetbrains.compose.web.dom.Text(" = 64")
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The flag that buys CIO 70 % on non-blocking handlers takes away 7.7× on a blocking one, and Netty — the cheapest engine above — is the worst here at its defaults, because ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("callGroupSize")
        }
        org.jetbrains.compose.web.dom.Text(" defaults to the processor count. Both defaults are coherent: CIO's 64 workers are the slack that blocking code lives on, and every non-blocking request pays for that slack. Neither engine is \"faster\"; they place their defaults differently between two regimes, and choosing an engine is choosing a regime.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("where-this-stops-being-true") }) {
        org.jetbrains.compose.web.dom.Text("Where this stops being true")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Under 8 connections instead of 64, CIO's queue polling nearly disappears — the queue is empty, the workers park — and the cost reappears as 10.8 context switches per request and 231 µs. Under 256, it is 1.19 switches and 110 µs with a p99 of 37 ms. The handoff is paid in every regime; only the currency changes.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The share, though, is a statement about load, not about CIO. Re-attributing the profiles of a real service — ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/konekt") {
            org.jetbrains.compose.web.dom.Text("konekt")
        }
        org.jetbrains.compose.web.dom.Text(", Ktor CIO with Exposed and Postgres on one core — gives the dispatcher queue 3.4 % of self CPU at 200 rps and 1.1 % at 50 rps, against 36 % on the stand at 43 k rps. A service that waits for a database pays for the handoff too, and does not notice.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("how-it-was-measured-and-why-not-in-rps") }) {
        org.jetbrains.compose.web.dom.Text("How it was measured, and why not in rps")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Run-to-run spread of throughput on this machine reaches ±13 % for one unchanged variant, while CPU microseconds per request repeat within 2–9 %. That, plus the concurrency ceiling above, is why the comparison is stated in CPU per request and rps is quoted beside it. Profiles and throughput come from separate windows: sampling at 1 ms costs between 0.4 % of throughput (CIO on ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("/echo")
        }
        org.jetbrains.compose.web.dom.Text(") and 8.3 % (Netty on the same endpoint, where there are twice as many requests to sample).")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("CPU per request is ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("utime+stime")
        }
        org.jetbrains.compose.web.dom.Text(" from ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("/proc/<pid>/stat")
        }
        org.jetbrains.compose.web.dom.Text(", which is tick-sampled, and the engines differ by an order of magnitude in context switches per request — so the accounting itself was checked against the scheduler's own counter (")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("sum_exec_runtime")
        }
        org.jetbrains.compose.web.dom.Text(" from ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("/proc/<pid>/task/*/schedstat")
        }
        org.jetbrains.compose.web.dom.Text(") on a machine whose kernel exposes it. Over the six runs of the table above, from 2 876 to 123 088 context switches per second, the two agree within 0.2 %.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The stand's own machine has no such counter, so there the per-process number was put against the busy time of the cores it is pinned to — those cores run nothing else, and their busy time cannot exceed eight seconds per second. It does: the process reads 8.54 cores where the cores themselves read exactly 8.00. The overstatement is 6.4–7.2 %, and it is the same at 25 context switches per second as at 298 000, so it is a property of that kernel rather than of an engine's threading. Every variant carries the same factor: the ratios in this post stand, and the absolute microseconds are about seven per cent high — 166 per request is nearer 155. The old, unpinned protocol is kept as a control and reproduces the earlier post's numbers — CIO at 34 495 rps against the published 33 935, coroutines owning 72.4 % of CPU against the published 73.9 % for all of kotlinx — which is also how you can tell the new numbers differ by protocol and not by the three engines now sharing a classpath.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("limitations") }) {
        org.jetbrains.compose.web.dom.Text("Limitations")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("One machine, a mobile CPU in WSL2, with no way to fix the clock. Ratios inside a process repeat; absolute throughput does not.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("HTTP/1.1 without TLS, keep-alive, responses of 14–1521 bytes, handlers in fractions of a millisecond. HTTP/2, TLS, WebSocket, streaming bodies and Tomcat were not measured.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Engines measured as they ship. Raising Netty's ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("callGroupSize")
            }
            org.jetbrains.compose.web.dom.Text(" would very likely remove its loss on ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("/blocking")
            }
            org.jetbrains.compose.web.dom.Text("; that was not run. ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("io.parallelism")
            }
            org.jetbrains.compose.web.dom.Text(" is process-wide — it changes ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("Dispatchers.IO")
            }
            org.jetbrains.compose.web.dom.Text(" for every coroutine in the service, not only for the engine.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("/blocking")
            }
            org.jetbrains.compose.web.dom.Text(" models blocking with ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("Thread.sleep")
            }
            org.jetbrains.compose.web.dom.Text(", which occupies a thread without burning CPU. A real JDBC call does both; those rows are about thread capacity, not about the cost of a query.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("The dispatcher table was measured on a different machine from the rest — two cores, another kernel, a background of 0.22 cores — so its microseconds are not comparable with the service numbers above. The ratios between its rows are the point.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("One profile run per configuration, with the repeated A/B series as the check that the per-request CPU numbers hold. Individual frame shares — Jetty's 20.4 % in ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("pthread_cond_signal")
            }
            org.jetbrains.compose.web.dom.Text(", say — were not repeated three times.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("I have not filed anything with Ktor about this. The question it raises — why the CIO engine dispatches into the shared ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("Dispatchers.IO")
            }
            org.jetbrains.compose.web.dom.Text(" rather than its own ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("limitedParallelism")
            }
            org.jetbrains.compose.web.dom.Text(" sized to the cores — is a question, not a defect report, and the tracker search I ran found only client-engine issues.")
        }
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Source: ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/zavarnik") {
            org.jetbrains.compose.web.dom.Text("github.com/youndie/zavarnik")
        }
        org.jetbrains.compose.web.dom.Text(" — ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("bench/")
        }
        org.jetbrains.compose.web.dom.Text(" for the service and the harness, ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("bench/profile/results/")
        }
        org.jetbrains.compose.web.dom.Text(" for the summary of every run cited here, ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("docs/research/research-engines.md")
        }
        org.jetbrains.compose.web.dom.Text(" for the fact tables with their addresses. The same stand's earlier measurements: ")
        com.varabyte.kobweb.silk.components.navigation.Link("/blog/user-code-share-of-a-ktor-service") {
            org.jetbrains.compose.web.dom.Text("User code is 1–4 % of a Ktor service's CPU")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
}
